import { FullStory } from '@fullstory/browser';
import { portalStoreToken, PortalUser } from '@servicetitan/cp-ui';
import { inject, injectable } from '@servicetitan/react-ioc';
import {
    ApiException,
    AuthApi,
    AuthenticationInfo,
    BrandingApi,
    ContactMethod,
    LegalAgreementApi,
    LoginType,
    PropertyOwnerType,
} from 'api';
import { BrandData } from 'api/src/portal-branding.api';
import Cookies from 'js-cookie';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { WebPortalStore } from '../../common/stores/portal.store';

export enum LoginResult {
    OK = 0,
    Invalid = 1,
    Restricted = 2,
    MfaRequired = 3,
    RateLimited = 4,
}

const loginDataCookieName = 'ST-Customer-Portal-Frontend';

export type LoginlessLoginType = 'EstimateLink' | 'PaymentLink' | 'ServiceAgreementLink';

export const SignerRole = {
    Customer: 'customer',
    Countersigner: 'countersigner',
} as const;

export type LoginlessSignerRole = (typeof SignerRole)[keyof typeof SignerRole];

/** Routing-only cookie set for expired links; it carries no session. */
const expiredLinkLoginType = 'ExpiredLink';

interface LoginlessLoginData {
    LoginType: LoginlessLoginType | typeof expiredLinkLoginType;
    Email: string | null;
    SignerRole?: LoginlessSignerRole;
}

@injectable()
export class PortalAuthStore {
    @observable authorizing = true;
    @observable authorized = false;
    @observable authorizedLoginless = false;
    @observable loginlessType?: LoginlessLoginType;
    @observable signerRole?: LoginlessSignerRole;
    @observable redirect?: string;
    @observable validateUserLoading = false;
    @observable brand: BrandData | undefined;
    @observable brandLoading: boolean = true;
    @observable mustAcceptLatestAgreement = false;
    @observable latestAgreementVersion: number | undefined;
    @observable mfaSessionToken?: string;
    @observable mfaCodeLength?: number;
    @observable mfaCodeLifetimeMinutes?: number;
    @observable mfaResendCooldownSeconds?: number;
    @observable mfaContactMethod?: ContactMethod;

    @computed get isAnonymousPayments() {
        return this.loginlessType === 'PaymentLink';
    }
    @computed get isAnonymousEstimates() {
        return this.loginlessType === 'EstimateLink';
    }
    @computed get isAnonymousServiceAgreements() {
        return this.loginlessType === 'ServiceAgreementLink';
    }

    constructor(
        @inject(AuthApi) private authApi: AuthApi,
        @inject(BrandingApi) private brandingApi: BrandingApi,
        @inject(LegalAgreementApi) private legalAgreementApi: LegalAgreementApi,
        @inject(portalStoreToken) public portalStore: WebPortalStore
    ) {
        makeObservable(this);
    }

    init = async (redirect?: string) => {
        runInAction(() => {
            this.authorized = false;
            this.redirect = redirect;
        });

        await this.validate();
    };

    validate = async () => {
        if (this.validateUserLoading) {
            return;
        }

        runInAction(() => {
            this.authorizing = true;
            this.validateUserLoading = true;
        });

        const user = await this.validateUser();

        if (user) {
            await this.loadAgreementStatus();

            runInAction(() => {
                this.authorizing = false;
                this.authorized = true;
            });

            this.portalStore.setUser(user);

            FullStory('setIdentity', {
                uid: user.id.toString(),
                properties: {
                    userName: user.name,
                    email: user.name,
                    tenantName: (window as any).PortalSettings?.tenantName ?? null,
                    tenantId: (window as any).PortalSettings?.tenantId ?? null,
                },
            });
        } else {
            const cookie = Cookies.get(loginDataCookieName);
            if (cookie) {
                try {
                    const loginData: LoginlessLoginData = JSON.parse(window.atob(cookie));
                    const { LoginType } = loginData;
                    if (LoginType !== expiredLinkLoginType) {
                        FullStory('setProperties', {
                            type: 'user',
                            properties: {
                                email: loginData.Email ?? null,
                                signerRole: loginData.SignerRole ?? null,
                                tenantName: (window as any).PortalSettings?.tenantName ?? null,
                                tenantId: (window as any).PortalSettings?.tenantId ?? null,
                            },
                        });
                        runInAction(() => {
                            this.authorizing = false;
                            this.authorizedLoginless = true;
                            this.loginlessType = LoginType;
                            this.signerRole = loginData.SignerRole;
                        });
                    }
                } catch {
                    // skip error
                }
            }
        }

        runInAction(() => {
            this.authorizing = false;
            this.validateUserLoading = false;
        });
    };

    validateAnyAccount = async () => {
        return this.validateUser(true);
    };

    login = async (
        username: string,
        password: string,
        keepSigned: boolean
    ): Promise<LoginResult> => {
        let result;
        try {
            result = await this.authApi.client.authenticate(
                new AuthenticationInfo({
                    username,
                    password,
                    keepSigned,
                })
            );
        } catch (e) {
            if (e instanceof ApiException && e.status === 429) {
                return LoginResult.RateLimited;
            }
            return LoginResult.Invalid;
        }

        if (result?.type === LoginType.MfaRequired) {
            runInAction(() => {
                this.mfaSessionToken = result.mfaSessionToken ?? undefined;
                this.mfaCodeLength = result.mfaCodeLength ?? undefined;
                this.mfaCodeLifetimeMinutes = result.mfaCodeLifetimeMinutes ?? undefined;
                this.mfaResendCooldownSeconds = result.mfaResendCooldownSeconds ?? undefined;
                this.mfaContactMethod = result.mfaContactMethod ?? undefined;
            });
            return LoginResult.MfaRequired;
        }

        if (result?.type === LoginType.Restricted) {
            return LoginResult.Restricted;
        }

        runInAction(() => {
            this.authorizing = true;
            this.authorized = false;
        });

        return LoginResult.OK;
    };

    loginWithGoogle = async (idToken: string): Promise<LoginResult> => {
        try {
            await this.authApi.client.authenticateWithGoogle(idToken);
        } catch {
            return LoginResult.Invalid;
        }

        runInAction(() => {
            this.authorizing = true;
            this.authorized = false;
        });

        return LoginResult.OK;
    };

    loginWithMicrosoft = async (token: string): Promise<LoginResult> => {
        try {
            await this.authApi.client.authenticateWithMicrosoft(token);
        } catch {
            return LoginResult.Invalid;
        }

        runInAction(() => {
            this.authorizing = true;
            this.authorized = false;
        });

        return LoginResult.OK;
    };

    logout = async () => {
        await this.authApi.client.logout().catch(() => null);

        runInAction(() => {
            this.authorizing = false;
            this.authorized = false;
            this.redirect = undefined;
            this.mustAcceptLatestAgreement = false;
            this.latestAgreementVersion = undefined;
        });

        this.portalStore.removeUser();
    };

    @action
    markAgreementAccepted = (): void => {
        this.mustAcceptLatestAgreement = false;
    };

    @action
    getBrand = async (entityId: string | undefined) => {
        try {
            if (!entityId) {
                return;
            }

            switch (this.loginlessType) {
                case 'EstimateLink':
                    this.brand = await this.brandingApi.getProposal(entityId);
                    break;
                case 'PaymentLink':
                    this.brand = await this.brandingApi.getInvoice(entityId);
                    break;
                case 'ServiceAgreementLink':
                    this.brand = await this.brandingApi.getServiceAgreement(Number(entityId));
                    break;
            }
        } catch {
            // skip error
        } finally {
            runInAction(() => {
                this.brandLoading = false;
            });
        }
    };

    private loadAgreementStatus = async (): Promise<void> => {
        try {
            const status = await this.legalAgreementApi.client.getStatus();
            runInAction(() => {
                this.mustAcceptLatestAgreement = !status.hasAccepted;
                this.latestAgreementVersion = status.latestVersion;
            });
        } catch {
            runInAction(() => {
                this.mustAcceptLatestAgreement = false;
                this.latestAgreementVersion = undefined;
            });
        }
    };

    private validateUser = async (anyAccount = false): Promise<PortalUser['data'] | undefined> => {
        try {
            const result = await this.authApi.client.validate();

            if (anyAccount || result.loginType === LoginType.Full) {
                return {
                    id: result.userId,
                    name: result.userName ?? '',
                    isResidential: result.propertyOwnerType === PropertyOwnerType.Residential,
                };
            }
        } catch {
            // skip
        }
    };
}
